/**
 * WebSocket 구독과 재연결 (계획서 §12.6).
 *
 * 재연결하면 무조건 REST snapshot 을 다시 읽는다. 끊긴 동안의 event 는 복구할 수 없다.
 * 조회가 실패하면 가상시계의 재생 여부와 무관하게 재시도한다 (1→2→4→8→10초).
 */

import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import type { WsEnvelope } from "@/types/api";

import { useRunStore } from "./runStore";

const RECONNECT_DELAY_MS = 2_000;
const RECOVERY_DELAY_MS = 1_000;
const MAX_RECOVERY_DELAY_MS = 10_000;

export type SnapshotSyncState = "ready" | "retrying" | "failed";

function isRetryable(cause: unknown): boolean {
  // 연결 실패·일시적 서버 오류만 재시도한다. 인증/권한/없는 run 은 사람 확인이 필요하다.
  return !(cause instanceof ApiError) || cause.status >= 500 ||
    cause.status === 408 || cause.status === 429;
}

function socketUrl(runId: string): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws/runs/${runId}`;
}

/** run 하나를 구독한다. `runId` 가 바뀌면 다시 연결한다. */
export function useRunConnection(runId: string | null): SnapshotSyncState {
  const setConnection = useRunStore((s) => s.setConnection);
  const applyEvent = useRunStore((s) => s.applyEvent);
  const reload = useRunStore((s) => s.reload);
  const [syncState, setSyncState] = useState<SnapshotSyncState>("ready");

  useEffect(() => {
    setSyncState("ready");
    if (!runId) return;

    let socket: WebSocket | null = null;
    let closed = false;
    let reconnectTimer: number | null = null;
    let recoveryTimer: number | null = null;
    let recoveryDelay = RECOVERY_DELAY_MS;
    let requiredSequence = 0;

    const isActive = (): boolean => !closed && useRunStore.getState().snapshot?.run_id === runId;

    const hasCurrentSnapshot = (): boolean => {
      const { snapshot, lastSequence } = useRunStore.getState();
      return snapshot?.ws_sequence !== undefined &&
        snapshot.ws_sequence >= Math.max(lastSequence, requiredSequence);
    };

    const clearRecovery = (): void => {
      if (recoveryTimer !== null) window.clearTimeout(recoveryTimer);
      recoveryTimer = null;
    };

    const recovered = (): void => {
      if (!isActive()) return;
      clearRecovery();
      recoveryDelay = RECOVERY_DELAY_MS;
      setSyncState("ready");
    };

    const scheduleRecovery = (cause?: unknown): void => {
      if (!isActive()) return;
      if (!isRetryable(cause)) {
        clearRecovery();
        setSyncState("failed");
        return;
      }
      setSyncState("retrying");
      // event 가 겹쳐 실패해도 재시도 타이머는 run 별로 하나만 만든다.
      if (recoveryTimer !== null) return;
      recoveryTimer = window.setTimeout(() => {
        recoveryTimer = null;
        reloadSnapshot();
      }, recoveryDelay);
      recoveryDelay = Math.min(recoveryDelay * 2, MAX_RECOVERY_DELAY_MS);
    };

    const reloadSnapshot = (): void => {
      if (!isActive()) return;
      void reload(runId)
        .then((applied) => {
          if (applied && hasCurrentSnapshot()) recovered();
          else scheduleRecovery(); // HTTP 성공이어도 오래된 응답은 복구가 아니다.
        })
        .catch(scheduleRecovery);
    };

    const checkEventRecovery = (): void => {
      // clock 부분 갱신이나 재전송만으로 실패한 전체 조회가 복구됐다고 판단하지 않는다.
      if (hasCurrentSnapshot()) recovered();
    };

    const connect = (): void => {
      if (closed) return;
      setConnection("connecting");
      const currentSocket = new WebSocket(socketUrl(runId));
      socket = currentSocket;
      const isCurrentSocket = (): boolean => isActive() && socket === currentSocket;

      currentSocket.onopen = () => {
        if (!isCurrentSocket()) return;
        setConnection("open");
        // 끊긴 동안의 event 는 복구할 수 없으므로 전체를 다시 읽는다 (§12.6).
        reloadSnapshot();
      };

      currentSocket.onmessage = (event: MessageEvent<string>) => {
        if (!isCurrentSocket()) return;
        try {
          const message = JSON.parse(event.data) as WsEnvelope | { type: "ping" } |
            { type: "connection.ready"; run_id: string; sequence: number };
          if (message.type === "ping") return;
          if (message.type === "connection.ready") {
            // onopen 조회와 구독 사이에 놓친 event 가 있을 수 있다. 연결 시 서버가
            // 알려준 번호까지 전체 상태를 복구하되, 제어 frame 을 운전 event 로 쓰지 않는다.
            if (message.run_id === runId && Number.isSafeInteger(message.sequence)) {
              requiredSequence = Math.max(requiredSequence, message.sequence);
              if (!hasCurrentSnapshot()) reloadSnapshot();
            }
            return;
          }
          if (message.run_id !== runId || message.sequence <= useRunStore.getState().lastSequence) return;
          void applyEvent(message).then(checkEventRecovery).catch(scheduleRecovery);
        } catch {
          // 형식이 깨진 event 는 버리고 snapshot 으로 복구한다.
          reloadSnapshot();
        }
      };

      currentSocket.onclose = () => {
        if (!isCurrentSocket()) return;
        setConnection("closed");
        if (reconnectTimer === null) {
          reconnectTimer = window.setTimeout(() => {
            reconnectTimer = null;
            connect();
          }, RECONNECT_DELAY_MS);
        }
      };

      currentSocket.onerror = () => {
        if (isCurrentSocket()) currentSocket.close();
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      clearRecovery();
      socket?.close();
    };
  }, [runId, applyEvent, reload, setConnection]);

  return syncState;
}
